// src/components/shared/DeckSpaceModal.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate }  from 'react-router-dom';
import { countries }     from 'countries-list';
import SubjectCareerMapModal        from '../pages/SubjectCareerMapModal';
import SubjectSuperCurricularMapModal from '../pages/SubjectSuperCurricularMapModal';
import { loadFeedItems, saveFeedItems, saveFetchTime, loadFetchTimes } from '../pages/enrichFeedDb';
import { loadCareerFeedItems, saveCareerFeedItems, loadCareerFetchTimes, saveCareerFetchTime } from '../pages/careerFeedDb';
import { avatarStyles }  from '../../constants/avatarStyles';
import { getAuthHeaders } from '../../api/assessmentApi';
import { useModalContext, getCategoryColor } from '../../constants/ModalContext';
import {
  FaGraduationCap, FaRss, FaDownload, FaRegCompass,
  FaBolt, FaPenSquare, FaCertificate, FaUserTie, FaCompass, FaEdit,
} from 'react-icons/fa';
import {
  getPortfolioJobs,
  getPortfolioStatus,
  getInterviewStatuses,
  getExtraCareerJobs,
  getRawAnswers,
  getSuperActivities, saveSuperActivities,
  getJobposts, saveJobposts,
  getSkillProgress,
  dashboardDb,
  getSubjectReasons, 
  saveSubjectReasons,
  getInsightsCache, getStrategyCache, getCalendarEvents,
  getRoadmap,
  saveRoadmap,
  getRoadmapReflections,
  saveRoadmapReflections,
  getChosenTopSubject,
  getActivityStatuses,
  getPsStatus,
  getUserSettings, saveUserSettings,
} from '../pages/dashboardDb';

/* ── Module-level constants & helpers ─────────────────────────────────────── */
const FACET_CACHE_TTL    = 14 * 24 * 60 * 60 * 1000;
const SUPER_CACHE_TTL    = 30 * 24 * 60 * 60 * 1000;
const JOBPOSTS_CACHE_TTL = 30 * 24 * 60 * 60 * 1000;

const normaliseCareerKey = (t) => (t || '').trim().replace(/\s+/g, ' ');

// Activity progress is stored as a number (0–100) by the SuperCurricular page.
// Older data may still be a legacy string — normalise both to a percent.
const normalizeProgressPercent = (val) => {
  if (typeof val === 'number' && !isNaN(val)) return Math.min(Math.max(val, 0), 100);
  if (val === 'completed') return 100;
  if (val === 'in_progress') return 40;
  return 0; // 'not_started', undefined, anything unexpected
};

const progressToStatusKey = (pct) =>
  pct >= 100 ? 'completed' : pct > 0 ? 'in_progress' : 'not_started';

const gradeLevels = [
  'Year 7','Year 8','Year 9','Year 10','Year 11','Year 12','Year 13',
  'Undergraduate Year 1','Undergraduate Year 2',
  'Undergraduate Year 3','Undergraduate Year 4',
];

const toolbarBtnStyle = {
  background: 'rgba(0,225,255,0.1)', color: '#00e1ff',
  border: '1px solid rgba(0,225,255,0.3)', padding: '6px 14px',
  borderRadius: '6px', cursor: 'pointer', fontSize: '1.05rem',
  fontWeight: 'bold', minWidth: '48px',
};

const getOptionLink = (n) => {
  if (n === 'A-Levels') return 'https://www.gov.uk/government/collections/gce-as-and-a-level-subject-content';
  if (n === 'IB')       return 'https://www.ibo.org/programmes/diploma-programme/curriculum/';
  if (n === 'AP')       return 'https://apstudents.collegeboard.org/courses/';
  return null;
};

const getAISearchLink = (subject) => {
  if (!subject) return '#';
  return `https://www.google.com/search?q=${encodeURIComponent(`${subject} degree future in AI era OR impact of artificial intelligence on ${subject} careers`)}`;
};

/* ── ReadonlyAverageProgressRing ─────────────────────────────────────────── */
function ReadonlyAverageProgressRing({ progress }) {
  const getRingStyle = () => {
    if (progress === 0)  return { color: '#ff4d4d' };
    if (progress <= 25)  return { color: '#ff8800' };
    if (progress <= 50)  return { color: '#f6fb00' };
    if (progress <= 75)  return { color: '#35a6f7' };
    return               { color: '#00ff26' };
  };
  const { color } = getRingStyle();
  const label = `${progress}%`;
  const radius = 24, strokeWidth = 2, circumference = 2 * Math.PI * radius;
  const isZero = progress === 0;
  const offset = isZero ? 0 : circumference - (circumference * progress) / 100;

  return (
    <div title={`Average self-rated progress: ${label}`}
      style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center', padding: '0 8px' }}
      className="progress-ring-wrapper readonly"
    >
      <svg width="86" height="86" viewBox="0 0 56 56">
        <circle cx="28" cy="28" r={radius} fill="none" stroke="#444" strokeWidth={strokeWidth} opacity="0.25" />
        <circle cx="28" cy="28" r={radius} fill="none" stroke={color}
          strokeWidth={isZero ? 0.5 : strokeWidth + 1}
          strokeDasharray={circumference} strokeDashoffset={offset}
          transform="rotate(-90 28 28)" strokeLinecap="round"
          filter={isZero ? 'none' : 'drop-shadow(0 0 2px currentColor) brightness(1.15)'} opacity={isZero ? 0.9 : 1}
        />
        <text x="28" y="30" textAnchor="middle" fill={isZero ? '#ff4d4d' : 'white'}
          fontSize="13" fontWeight="bold" dominantBaseline="middle">{label}</text>
      </svg>
    </div>
  );
}

/* ── Section "Add" Button ────────────────────────────────────────────── */
function AddSectionButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600,
        color: '#40c4ff', background: 'rgba(2,139,232,0.10)',
        border: '1px solid rgba(2,139,232,0.4)', borderRadius: '999px',
        cursor: 'pointer', transition: 'all 0.18s ease', whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(2,139,232,0.22)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(2,139,232,0.10)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <span style={{ fontSize: '1rem', lineHeight: 1 }}>+</span> {label}
    </button>
  );
}

/* ── Star Rating Component ───────────────────────────────────────────── */
function StarRating({ rating, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '4px', fontSize: '1.8rem', cursor: 'pointer' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          onClick={() => onChange(star)}
          style={{ color: star <= rating ? '#ffd700' : '#555', transition: 'color 0.1s' }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

// ── Memoized Job Card ────────────────────────────────────────────────
const JobCard = React.memo(({
  job,
  interviewStatus,
  extraCareerJobsList,
  jobSubjectMap,
  chosenTopSubject,
  topScores,
  topPart3Scores,
  userEmail,
  skillProgress,
  jobFeedOpen,
  onNavigate,
  onToggleFeed,
  onLoadFeed,
  renderProfeedPanel,
}) => {
  const jobId = String(job.jobpost_id);
  const isExtraJob = extraCareerJobsList.some(j => String(j.jobpost_id) === jobId);
  const subjectForJob = isExtraJob ? null : (jobSubjectMap[jobId] || null);

  const statusConfig = {
    not_started: { label: 'Not Started', color: '#888888ff', bg: 'rgba(158,158,158,0.14)', border: 'rgba(158,158,158,0.3)' },
    in_progress: { label: 'In Progress', color: '#00dbff',   bg: 'rgba(33,150,243,0.18)', border: 'rgba(33,150,243,0.4)' },
    completed:   { label: 'Completed',   color: '#00ff00',   bg: 'rgba(76,175,80,0.18)',  border: 'rgba(76,175,80,0.4)'  },
  };
  const sc = statusConfig[interviewStatus] || statusConfig.not_started;

  const handleNavigate = () => {
    onNavigate('/CareerDashboard', {
      sidebarItem: 'compass',
      preselectedJobId: job.jobpost_id,
      bypassSubjectFilter: isExtraJob,
      directToCompass: true,
      preselectedSubject: subjectForJob || chosenTopSubject,
      fromDeckSpace: true,
      sortedScores: topScores || [],
      sortedPart3Scores: topPart3Scores || [],
      chosenTopSubject,
      userEmail,
    });
  };

  return (
    <div
      onClick={handleNavigate}
      style={{
        flex: '0 0 340px',
        background: 'rgba(30,40,60,0.75)',
        border: '1px solid rgba(54,167,248,0.4)',
        borderRadius: '14px',
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.1rem',
        transition: 'all 0.22s ease',
        cursor: 'pointer',
        minWidth: '350px',
      }}
      className="compass-job-card"
    >
      <h4 style={{ margin: 0, fontSize: '1.28rem', color: '#ffffff', fontWeight: 100 }}>
        <FaCompass /> {job.title}
      </h4>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0rem 0', color: '#01d4f2', maxWidth: '350px' }}>
        Skills Progress
        <ReadonlyAverageProgressRing progress={skillProgress} />
      </div>

      <div  style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexDirection: 'column', marginTop: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* InterviewPro button */}
        <button
        className="interviewpro-button"
          onClick={e => {
            e.stopPropagation();
            onNavigate('/CareerDashboard', {
              sidebarItem: 'interviewx',
              preselectedJobId: job.jobpost_id,
              fromDeckSpace: true,
            });
          }}
          style={{
            flex: 1,
            minWidth: '140px',
            padding: '10px 14px',
            background: sc.bg,
            border: `1px solid ${sc.border}`,
            borderRadius: '10px',
            color: sc.color,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            transition: 'all 0.18s ease',
          }}
        >
          <FaUserTie className="combined" style={{ color: 'white' }} />
          <span className="combined" style={{ flex: 1, textAlign: 'left', color: 'white' }}>InterviewPro</span>
          <div style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '600', backgroundColor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap' }}>
            {sc.label}
          </div>
        </button>

        {/* ProFeed RSS */}
        <button
          className="interviewpro-button"
          onClick={e => {
            e.stopPropagation();
            onToggleFeed(job.jobpost_id);
            if (!jobFeedOpen) onLoadFeed(job.title);
          }}
          style={{
            flex: 1,
            minWidth: '140px',
            padding: '10px 14px',
            background: '#028BE808',
            border: '1px solid #028BE8',
            borderRadius: '10px',
            color: '#028BE8',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.9rem',
            transition: 'all 0.18s ease',
          }}
        >
          <FaRss />
          {jobFeedOpen ? 'Hide ProFeed' : 'Latest Articles'}
        </button>

        {jobFeedOpen && (
          <div style={{ marginTop: '-10px', padding: '10px', background: '#028BE808', borderRadius: '10px', border: '1px solid #028BE8' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', color: '#028BE8', fontWeight: 600, fontSize: '0.85rem' }}>
              <FaRss /> Latest Articles
            </div>
            {renderProfeedPanel}
          </div>
        )}
      </div>
    </div>
  );
});

// ── Memoized Activity Card ────────────────────────────────────────────
const ActivityCard = React.memo(({
  event,
  activityStatuses,
  topScores,
  topPart3Scores,
  chosenTopSubject,
  userEmail,
  onNavigate,
}) => {
  const start = new Date(event.startDate);
  const end = event.endDate ? new Date(event.endDate) : start;
  const activityId = event.supercurricular_id || event.id;
const progressPct = normalizeProgressPercent(activityStatuses[activityId]);
const typeColor = getCategoryColor(event.category || 'other');
const statusConfig = {
  not_started: { label: 'Not Started', color: '#888888ff', bg: 'rgba(158,158,158,0.14)', border: 'rgba(158,158,158,0.3)' },
  in_progress: { label: 'In Progress', color: '#00dbff',   bg: 'rgba(33,150,243,0.18)', border: 'rgba(33,150,243,0.4)' },
  completed:   { label: 'Completed',   color: '#00ff00',   bg: 'rgba(76,175,80,0.18)',  border: 'rgba(76,175,80,0.4)'  },
};
const sc = statusConfig[progressToStatusKey(progressPct)];

  // Route by category, mirroring SelectedEventPopup in ModalContext.js —
  // this card previously always sent every category to SuperCurricular,
  // which is why Knowledge/AIXcel cards silently landed on the wrong tab.
  const categoryLower = event.category?.toLowerCase();
  const isKnowledgeFreeCourse = categoryLower === 'free course';
  const isAiSkillCourse = categoryLower === 'ai skill course';

  const handleClick = () => {
    if (isKnowledgeFreeCourse) {
      onNavigate('/dashboard_enrich', {
        sidebarItem: 'knowledge',
        preselectedKnowledgeSubject: event.universitySubject,
        preselectedFreeCourseTitle: event.title,
        fromDeckSpace: true,
        sortedScores: topScores || [],
        sortedPart3Scores: topPart3Scores || [],
        chosenTopSubject,
        userEmail,
      });
    } else if (isAiSkillCourse) {
      onNavigate('/LeaderDashboard', {
        sidebarItem: 'aixcel',
        preselectedFacet: event.facet,
        preselectedCourseType: event.courseType,
        preselectedFreeCourseTitle: event.title,
        fromDeckSpace: true,
      });
    } else {
      // Intellectual Pursuits / Clubs & Societies / Work Experience
      onNavigate('/dashboard_enrich', {
        sidebarItem: 'supercurricular',
        preselectedSuperSubject: event.universitySubject || event.category,
        preselectedCategory: event.category,
        preselectedActivityId: event.supercurricular_id || event.id,
        scrollToActivity: true,
        fromDeckSpace: true,
        sortedScores: topScores || [],
        sortedPart3Scores: topPart3Scores || [],
        chosenTopSubject,
        userEmail,
      });
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        flex: '0 0 350px',
        background: 'rgba(30,40,60,0.7)',
        border: `1px solid ${typeColor.border}`,
        borderRadius: '12px',
        padding: '1.4rem 1.6rem',
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        minHeight: '180px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      className="supercurr-event-card"
    >
      <div style={{ alignSelf: 'flex-start', background: typeColor.bg, color: typeColor.border, padding: '5px 12px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: '600', border: `1px solid ${typeColor.border}`, marginBottom: '0.9rem' }}>
        {event.category || 'Activity'}
      </div>
      <h4 style={{ margin: '0 0 0.9rem 0', fontSize: '1.18rem', lineHeight: 1.4 }}><FaBolt /> {event.title}</h4>
      <div style={{ marginTop: 'auto' }}>
        <div style={{ fontSize: '0.88rem', color: '#ccc', marginBottom: '0.7rem' }}>
          {start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          {end.getTime() !== start.getTime() && <> – {end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '100px', padding: '8px 16px', borderRadius: '9px', fontSize: '0.8rem', fontWeight: '600', textTransform: 'capitalize', backgroundColor: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
          {sc.label}{progressPct > 0 && progressPct < 100 ? ` · ${progressPct}%` : ''}
        </div>
      </div>
    </div>
  );
});

// ── Memoized ProFeed Panel ────────────────────────────────────────────
const ProFeedPanel = React.memo(({ jobTitle, items, isLoading, onViewFull }) => {
  const nt = normaliseCareerKey(jobTitle);
  return (
    <div className="invisible-scroll" style={{ maxHeight: '200px', overflowY: 'auto' }}>
      {isLoading ? <p style={{ color: '#aaa', fontStyle: 'italic' }}>Loading latest articles…</p>
      : items.length === 0 ? <p style={{ color: '#aaa', fontStyle: 'italic' }}>No recent articles found.</p>
      : (
        <>
          {items.map((item, idx) => (
            <div key={item.guid || idx} style={{ marginBottom: '10px', padding: '8px 10px', background: '#028BE808', borderRadius: '8px', border: 'none' }}>
              <a href={item.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#0076c4', textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.4, display: 'block', marginBottom: '4px' }}>
                {item.title || 'Untitled'}
              </a>
              <div style={{ fontSize: '0.78rem', color: '#888' }}>{item.source || ''}{item.pub_date ? ` · ${new Date(item.pub_date).toLocaleDateString('en-GB')}` : ''}</div>
            </div>
          ))}
          <button
            onClick={onViewFull}
            style={{ marginTop: '8px', width: '100%', padding: '7px', background: '#028BE808', border: '1px solid #028BE8', borderRadius: '7px', color: '#028BE8', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            <FaRss /> View full ProFeed →
          </button>
        </>
      )}
    </div>
  );
});

// ── Memoized AcadFeed Panel ────────────────────────────────────────────
const AcadFeedPanel = React.memo(({ subject, items, isLoading, onViewFull }) => (
  <div className="invisible-scroll" style={{ maxHeight: '200px', overflowY: 'auto' }}>
    {isLoading ? <p style={{ color: '#aaa', fontStyle: 'italic' }}>Loading latest articles…</p>
    : items.length === 0 ? <p style={{ color: '#aaa', fontStyle: 'italic' }}>No recent articles found.</p>
    : (
      <>
        {items.map((item, idx) => (
          <div key={item.guid || idx} style={{ marginBottom: '10px', padding: '8px 10px', background: 'rgba(54,167,248,0.08)', borderRadius: '8px', border: '1px solid rgba(54,167,248,0.25)' }}>
            <a className="acadfeed-panel" href={item.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#36a7f8', textDecoration: 'none', fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.4, display: 'block', marginBottom: '4px' }}>
              {item.title || 'Untitled'}
            </a>
            <div style={{ fontSize: '0.78rem', color: '#888' }}>{item.source || ''}{item.pub_date ? ` · ${new Date(item.pub_date).toLocaleDateString('en-GB')}` : ''}</div>
          </div>
        ))}
        <button
          onClick={onViewFull}
          style={{ marginTop: '8px', width: '100%', padding: '7px', background: 'rgba(54,167,248,0.15)', border: '1px solid #36a7f8', borderRadius: '7px', color: '#36a7f8', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
        >
          <FaRss /> View full AcadFeed →
        </button>
      </>
    )}
  </div>
));

// Exported so the export engine can label roadmapReflections keys without
// duplicating this list — the prompt text lives in exactly one place.
export const ROADMAP_REFLECTION_QUESTIONS = [
  { key: 'priority',   prompt: 'Which recommendation excites you most, and why?', type: 'textarea' },
  { key: 'challenge',  prompt: 'Which milestone feels most challenging?',          type: 'textarea' },
  { key: 'changes',    prompt: 'What has changed since your last roadmap?',        type: 'textarea' },
  { key: 'obstacles',  prompt: 'What is your biggest obstacle right now?',         type: 'textarea' },
  { key: 'remember',   prompt: 'What should your AI remember?',                    type: 'textarea' },
  { key: 'confidence', prompt: 'How confident are you?',                          type: 'rating' },
];

/* ── Main component ───────────────────────────────────────────────────────── */
const DeckSpaceModal = React.memo(function DeckSpaceModal({ userId }) {
  const { closeModal } = useModalContext();
  const navigate = useNavigate();

  /* parent-view context — read from localStorage (set by ParentMentorGuard) */
  const viewingChildId   = localStorage.getItem('viewingChildId');
  const isParentView     = !!viewingChildId;
  const viewingChildName = localStorage.getItem('viewingChildName') || 'Student';
  const effectiveUserId  = viewingChildId || userId || localStorage.getItem('userId') || '';

  /* navigate helper — mirrors Dashboard's navigateWithViewMode, also closes modal */
  const modalNavigate = useCallback((path, state = {}) => {
    if (viewingChildId) {
      const childName = localStorage.getItem('viewingChildName') || '';
      navigate(
        { pathname: path, search: `?viewingChildId=${encodeURIComponent(viewingChildId)}&childName=${encodeURIComponent(childName)}` },
        { state: { ...state, viewingChildId, childName } }
      );
    } else {
      navigate(path, { state });
    }
    closeModal();
  }, [navigate, closeModal, viewingChildId]);

const [chosenTopSubject, setChosenTopSubject] = useState(null);

useEffect(() => {
  if (!effectiveUserId) return;
  (async () => {
    const subject = await getChosenTopSubject(effectiveUserId);
    setChosenTopSubject(subject);
  })();
}, [effectiveUserId]);

const [selectedGrade, setSelectedGrade] = useState('Year 7');
const [selectedCountry, setSelectedCountry] = useState('');

// Load grade and country from Dexie
useEffect(() => {
  if (!effectiveUserId) return;
  (async () => {
    const settings = await getUserSettings(effectiveUserId);
    setSelectedGrade(settings.grade || 'Year 7');
    setSelectedCountry(settings.country || '');
  })();
}, [effectiveUserId]);

// Save grade and country when they change (debounced)
useEffect(() => {
  if (!effectiveUserId) return;
  const timeout = setTimeout(async () => {
    await saveUserSettings(effectiveUserId, {
      grade: selectedGrade,
      country: selectedCountry,
      // notes field omitted – no longer stored
    });
  }, 500);
  return () => clearTimeout(timeout);
}, [selectedGrade, selectedCountry, effectiveUserId]);

const [calendarEvents, setCalendarEvents] = useState([]);
useEffect(() => {
  if (!effectiveUserId) return;
  let cancelled = false;
  (async () => {
    const data = await getCalendarEvents(effectiveUserId);
    // Exclude PortfolioX job-tracker events (deadlines/interviews synced from
    // CareerDashboard's "Key Dates") — deckSPACE's SuperCurricular carousel
    // and roadmap context are meant to reflect academic/enrichment activities
    // only. Job-tracker events are tagged both by a stable id prefix and by
    // category, so filter on either to be safe against future changes.
    const superCurricularOnly = (data || []).filter(
      (e) => !String(e.id).startsWith('jobtracker-') && e.category?.toLowerCase() !== 'job application'
    );
    if (!cancelled) setCalendarEvents(superCurricularOnly);
  })();
  return () => { cancelled = true; };
}, [effectiveUserId]);

const [portfolioXJobs, setPortfolioXJobs] = useState([]);
const [portfolioXStatus, setPortfolioXStatus] = useState('not_started');
const [interviewStatuses, setInterviewStatuses] = useState({});
const [extraCareerJobsList, setExtraCareerJobsList] = useState([]);
const [skillProgressAverages, setSkillProgressAverages] = useState({}); // jobpost_id -> avg %
const [jobSubjectMap, setJobSubjectMap] = useState({}); // jobpost_id -> subject

const userEmail    = localStorage.getItem('userEmail') || 'User';
const userUsername = localStorage.getItem('userUsername') || null;
const currentAvatar = localStorage.getItem(`user_avatar_${userId}`) || 'fa-avatar-1';

const [selectedRoadmapJob, setSelectedRoadmapJob] = useState(null);

const [roadmapContent, setRoadmapContent] = useState('');
const [reflections, setReflections] = useState({});

const reflectionQuestions = ROADMAP_REFLECTION_QUESTIONS;

const handleReflectionChange = (key, value) => {
  setReflections(prev => ({ ...prev, [key]: value }));
};

const handleSaveReflections = useCallback(async () => {
  await saveRoadmapReflections(effectiveUserId, 'career_3_year_plan', reflections);
  alert('Reflections saved — this data will make future roadmaps much smarter.');
}, [effectiveUserId, reflections]);

// Generate Roadmap - Enhanced prompt with rich reflection context
const handleGenerateRoadmap = useCallback(async () => {
  if (!selectedRoadmapJob) {
    alert("Please select a target job first.");
    return;
  }

  // Auto-save reflections
  try {
    await saveRoadmapReflections(effectiveUserId, 'career_3_year_plan', reflections);
  } catch (e) {
    console.warn("Could not save reflections", e);
  }

  const countryName = selectedCountry && countries[selectedCountry] 
    ? countries[selectedCountry].name 
    : selectedCountry || 'unknown country';

  const superActs = calendarEvents.length > 0
    ? calendarEvents.map(ev => `${ev.title} (${ev.category || 'general'})`).join(' • ')
    : 'none planned yet';

  let studyStrategy = '';
  let careerInsights = '';

  try {
    const stratRow = await getStrategyCache(effectiveUserId, isParentView && viewingChildId ? 'parent_view' : 'self');
    studyStrategy = stratRow?.strategy || '';
    
    const insightRow = await getInsightsCache(effectiveUserId, isParentView && viewingChildId ? 'child' : 'career');
    careerInsights = insightRow?.insights || '';
  } catch (e) {
    console.warn("Failed to load additional context", e);
  }

  // Rich reflection context
  const reflectionText = Object.entries(reflections)
    .filter(([_, v]) => v && String(v).trim().length > 0)
    .map(([key, value]) => {
      const label = reflectionQuestions.find(q => q.key === key)?.prompt || key;
      return `• ${label}\n  ${value}`;
    })
    .join('\n\n');

  const prompt = `You are Grok, acting as the dedicated long-term academic and career advisor for this student.

**Student Profile**
- Current Level: ${selectedGrade} in ${countryName}
- Chosen Higher Education Focus: ${chosenTopSubject || 'Undecided'}
- Target Early Career Goal: ${selectedRoadmapJob.title}
- Planned SuperCurricular Activities: ${superActs}

**Additional Context**
Study Strategy: ${studyStrategy || 'None saved yet'}
Career Insights: ${careerInsights || 'None saved yet'}

**Student Reflections** (Very Important - incorporate these deeply):
${reflectionText || 'No reflections provided yet.'}

---

**Task:**
Create a **highly personalised, realistic, and motivating 3-year academic and career development plan** to help this student excel.

Structure the plan clearly as:

**Year 1: Build Strong Foundation**
**Year 2: Develop Deep Expertise**
**Year 3: Launch & Achieve**

For each year, include:
- Key academic goals
- Recommended supercurricular / experiential activities
- Skill development priorities
- Milestones and timelines
- How to address the student’s own reflections and challenges

Make the plan inspiring yet practical, taking into account their excitement, obstacles, confidence level, and personal context.`;

  await navigator.clipboard.writeText(prompt);
  
  alert(`✅ Enhanced prompt for "${selectedRoadmapJob.title}" copied!\n\nOpening Grok...`);
  window.open('https://grok.com', '_blank');
}, [selectedRoadmapJob, reflections, effectiveUserId, selectedGrade, selectedCountry, chosenTopSubject, calendarEvents, viewingChildId, isParentView]);

  /* ── Skeleton ready ──────────────────────────────────────────────────── */
  const [deckSpaceReady, setDeckSpaceReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDeckSpaceReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  // ── Subject reasoning (Why do you like this subject?) ──
const SUBJECT_REASON_TAGS = [
  "I enjoy it 😊",
  "I'm good at it 💪",
  "It fits my future plans 🚀",
  "Someone recommended it 🤝",
  "It sparked my interest ✨",
  "I'm curious 🌱",
];

const [subjectReasons, setSubjectReasons] = useState({}); 
const [reasonEditorSubject, setReasonEditorSubject] = useState(null);
const [reasonDraftTags, setReasonDraftTags] = useState([]);
const [reasonDraftText, setReasonDraftText] = useState('');

const handleSaveGrade = (g) => {
  setSelectedGrade(g);
  // saveUserSettings will be triggered by the debounced effect
};

 
  /* ── Activity statuses ───────────────────────────────────────────────── */
const [activityStatuses, setActivityStatuses] = useState({});

useEffect(() => {
  if (!effectiveUserId) return;
  (async () => {
    const data = await getActivityStatuses(effectiveUserId);
    setActivityStatuses(data);
  })();
}, [effectiveUserId]);

const [psStatus, setPsStatus] = useState('not_started');

useEffect(() => {
  if (!effectiveUserId || !chosenTopSubject) return;
  (async () => {
    const status = await getPsStatus(effectiveUserId, chosenTopSubject);
    setPsStatus(status);
  })();
}, [effectiveUserId, chosenTopSubject]);


const [topScores, setTopScores] = useState([]);
  const [topPart3Scores, setTopPart3Scores] = useState([]);

  /* ── Consolidated Dexie load: portfolio jobs, interview statuses, extra
     career jobs, top subject scores, per-job subject map, skill progress ── */
  useEffect(() => {
    if (!effectiveUserId) return;
    let cancelled = false;

    (async () => {
      const computeTop = (answers, category) => {
        const filtered = (answers || []).filter(a => a.category === category);
        const facetMap = filtered.reduce((acc, a) => {
          const f = a.facet || 'Unknown';
          if (!acc[f]) acc[f] = { scores: [], taxonomy: new Set(), facetNote: a.facet_note || null };
          acc[f].scores.push(a.score);
          if (Array.isArray(a.taxonomy)) a.taxonomy.forEach(t => acc[f].taxonomy.add(t));
          return acc;
        }, {});
        const all = Object.entries(facetMap)
          .filter(([, d]) => d.scores.length > 0)
          .map(([facet, d]) => ({
            _id: facet,
            averageScore: d.scores.reduce((s, v) => s + v, 0) / d.scores.length,
            taxonomy: Array.from(d.taxonomy),
            facetNote: d.facetNote,
          }))
          .sort((a, b) => b.averageScore - a.averageScore);
        const result = [];
        let i = 0;
        while (i < all.length && (result.length < 3 || all[i].averageScore === result[2]?.averageScore)) {
          result.push(all[i++]);
        }
        return result;
      };

const [jobs, iStatuses, extraJobs, subjectAnswers, part3Answers, jobpostRows, pfStatus] = await Promise.all([
        getPortfolioJobs(effectiveUserId),
        getInterviewStatuses(effectiveUserId),
        getExtraCareerJobs(effectiveUserId),
        getRawAnswers(effectiveUserId, 'Subject'),
        getRawAnswers(effectiveUserId, 'SubjectPart3'),
        dashboardDb.jobposts.where('userId').equals(effectiveUserId).toArray(),
        getPortfolioStatus(effectiveUserId),
      ]);
      if (cancelled) return;

      setPortfolioXJobs(jobs);
      setInterviewStatuses(iStatuses);
      setExtraCareerJobsList(extraJobs);
      setPortfolioXStatus(pfStatus);
      setTopScores(computeTop(subjectAnswers?.data, 'Part II'));
      setTopPart3Scores(computeTop(part3Answers?.data, 'Part III'));

      // Build jobpost_id -> subject map from cached per-subject jobpost rows
      const subjMap = {};
      jobpostRows.forEach((row) => {
        (row.data || []).forEach((j) => {
          if (j?.jobpost_id != null) subjMap[String(j.jobpost_id)] = row.subject;
        });
      });
      setJobSubjectMap(subjMap);

      // Average skill progress per portfolio job
      const progressEntries = await Promise.all(
        jobs.map(async (job) => {
          if (!job.required_skills?.length) return [job.jobpost_id, 0];
          const vals = await Promise.all(
            job.required_skills.map((skill) => getSkillProgress(effectiveUserId, job.jobpost_id, skill))
          );
          const avg = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
          return [job.jobpost_id, avg];
        })
      );
      if (!cancelled) setSkillProgressAverages(Object.fromEntries(progressEntries));
    })();

    return () => { cancelled = true; };
  }, [effectiveUserId]);

  // Load subject reasons
useEffect(() => {
  if (!effectiveUserId) return;
  (async () => {
    try {
      const data = await getSubjectReasons(effectiveUserId);
      setSubjectReasons(data);
    } catch (err) {
      console.warn('Could not load subject reasons:', err.message);
    }
  })();
}, [effectiveUserId]);



// Load saved roadmap + reflections
useEffect(() => {
  if (!effectiveUserId) return;

  (async () => {
    try {
      const rm = await getRoadmap(effectiveUserId, 'career_3_year_plan');
      
      if (rm?.roadmapContent) {
        setRoadmapContent(rm.roadmapContent);
      } else {
        setRoadmapContent('');
      }

      const refs = await getRoadmapReflections(effectiveUserId);
      setReflections(refs || {});
    } catch (err) {
      console.error("Failed to load roadmap:", err);
      setRoadmapContent('');
    }
  })();
}, [effectiveUserId]);

// Auto-save roadmap content (improved)
useEffect(() => {
  if (!effectiveUserId) return;

  const timeout = setTimeout(async () => {
    try {
      await saveRoadmap(effectiveUserId, 'career_3_year_plan', roadmapContent || '', {
        subject: chosenTopSubject,
        grade: selectedGrade,
        country: selectedCountry,
        targetJob: selectedRoadmapJob?.title || null,
        updatedAt: new Date().toISOString()
      });
      // Optional: console.log('Roadmap auto-saved');
    } catch (err) {
      console.error('Failed to auto-save roadmap:', err);
    }
  }, 1000); // 1 second debounce

  return () => clearTimeout(timeout);
}, [
  roadmapContent, 
  effectiveUserId, 
  chosenTopSubject, 
  selectedGrade, 
  selectedCountry, 
  selectedRoadmapJob?.title   // only title, not whole object
]);

useEffect(() => {
  if (!effectiveUserId) return;

  const timeout = setTimeout(async () => {
    try {
      await saveRoadmapReflections(effectiveUserId, 'career_3_year_plan', reflections);
    } catch (err) {
      console.error('Failed to auto-save reflections:', err);
    }
  }, 800);

  return () => clearTimeout(timeout);
}, [reflections, effectiveUserId]);

  const getAverageSkillProgressForJob = useCallback((jobpostId) => {
    return skillProgressAverages[jobpostId] || 0;
  }, [skillProgressAverages]);



  /* ── Facet tooltip ───────────────────────────────────────────────────── */
  const [modalTooltip, setModalTooltip]         = useState({ visible: false, x: 0, y: 0 });
  const [modalCurrentFacet, setModalCurrentFacet] = useState(null);
  const [modalActiveTab, setModalActiveTab]     = useState('A-Levels');
  const [facetData, setFacetData]               = useState({ note: '', options: [] });
  const [acadFeedOpenModal, setAcadFeedOpenModal] = useState(false);

  const fetchFacetData = useCallback(async (facetName) => {
    if (!facetName) return;
    setModalCurrentFacet(facetName);
    const cacheKey = `facet_${facetName}`;
    const now = Date.now();
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const p = JSON.parse(cached);
        if (now - p.timestamp < FACET_CACHE_TTL) { setFacetData(p.data); return; }
      } catch { localStorage.removeItem(cacheKey); }
    }
    try {
      const res = await fetch(`/api/questions/facets/${encodeURIComponent(facetName)}`, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      const full = { note: data?.facet_note || '', options: data?.options || [] };
      setFacetData(full);
      localStorage.setItem(cacheKey, JSON.stringify({ data: full, timestamp: now }));
    } catch { setFacetData({ note: '', options: [] }); }
  }, []);

  /* ── AcadFeed ────────────────────────────────────────────────────────── */
  const [acadFeedItems, setAcadFeedItems]               = useState({});
  const [acadFeedLoading, setAcadFeedLoading]           = useState(false);
  const [acadFeedFetchedSubjects, setAcadFeedFetchedSubjects] = useState(new Map());

  const loadAcadFeedForSubject = useCallback(async (subject) => {
    if (!subject || !userId) return;
    const fetched = acadFeedFetchedSubjects.get(subject);
    if (fetched && Date.now() - fetched < 3600000) return;
    setAcadFeedLoading(true);
    const now = Date.now();
    try {
      const [allItems, fetchTimes] = await Promise.all([loadFeedItems(userId), loadFetchTimes(userId)]);
      const lastFetch = fetchTimes[subject] || 0;
      const existing  = Object.values(allItems).filter(i => i.subject === subject);
      if (now - lastFetch < 86400000 && existing.length > 0) {
        setAcadFeedItems(p => ({ ...p, [subject]: existing.sort((a,b) => new Date(b.pub_date) - new Date(a.pub_date)).slice(0,5) }));
        setAcadFeedFetchedSubjects(p => new Map([...p, [subject, now]]));
        return;
      }
      let newest = null;
      existing.forEach(i => { if (i.pub_date) { const d = new Date(i.pub_date); if (!isNaN(d) && (!newest || d > newest)) newest = d; } });
      const token = localStorage.getItem('token');
      if (!token) return;
      const params = newest ? { subject, since: newest.toISOString() } : { subject, limit: 250 };
      const res = await fetch(`/api/rss/items?${new URLSearchParams(params)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('acad fetch failed');
      const newA = await res.json();
      if (newA.length) await saveFeedItems(userId, newA);
      await saveFetchTime(userId, subject, now);
      const updatedAll = await loadFeedItems(userId);
      const combined = Object.values(updatedAll).filter(i => i.subject === subject).sort((a,b) => new Date(b.pub_date) - new Date(a.pub_date)).slice(0,5);
      setAcadFeedItems(p => ({ ...p, [subject]: combined }));
      setAcadFeedFetchedSubjects(p => new Map([...p, [subject, now]]));
    } catch (err) {
      console.warn('[DeckSpaceModal AcadFeed]', err.message);
      setAcadFeedItems(p => ({ ...p, [subject]: [] }));
    } finally { setAcadFeedLoading(false); }
  }, [userId, acadFeedFetchedSubjects]);

  /* ── ProFeed ─────────────────────────────────────────────────────────── */
  const [jobFeedItems, setJobFeedItems]               = useState({});
  const [jobFeedLoading, setJobFeedLoading]           = useState(false);
  const [jobFeedFetchedTitles, setJobFeedFetchedTitles] = useState(new Map());
  const [jobFeedOpenCards, setJobFeedOpenCards]       = useState({});

  const loadProfeedForJob = useCallback(async (jobTitle) => {
    if (!jobTitle || !userId) return;
    const nt = normaliseCareerKey(jobTitle);
    const fetched = jobFeedFetchedTitles.get(nt);
    if (fetched && Date.now() - fetched < 3600000) return;
    setJobFeedLoading(true);
    const now = Date.now();
    try {
      const [allItems, fetchTimes] = await Promise.all([loadCareerFeedItems(userId), loadCareerFetchTimes(userId)]);
      const lastFetch = fetchTimes[nt] || 0;
      const existing  = Object.values(allItems).filter(i => normaliseCareerKey(i.category_name) === nt);
      if (now - lastFetch < 86400000 && existing.length > 0) {
        setJobFeedItems(p => ({ ...p, [nt]: existing.sort((a,b) => new Date(b.pub_date) - new Date(a.pub_date)).slice(0,5) }));
        setJobFeedFetchedTitles(p => new Map([...p, [nt, now]]));
        return;
      }
      let newest = null;
      existing.forEach(i => { if (i.pub_date) { const d = new Date(i.pub_date); if (!isNaN(d) && (!newest || d > newest)) newest = d; } });
      const token = localStorage.getItem('token');
      if (!token) return;
      const params = newest ? { career: nt, since: newest.toISOString() } : { career: nt, limit: 20 };
      const res = await fetch(`/api/rss/career-items?${new URLSearchParams(params)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('profeed fetch failed');
      const newA = await res.json();
      if (newA.length) await saveCareerFeedItems(userId, newA);
      await saveCareerFetchTime(userId, nt, now);
      const updatedAll = await loadCareerFeedItems(userId);
      const combined = Object.values(updatedAll).filter(i => normaliseCareerKey(i.category_name) === nt).sort((a,b) => new Date(b.pub_date) - new Date(a.pub_date)).slice(0,5);
      setJobFeedItems(p => ({ ...p, [nt]: combined }));
      setJobFeedFetchedTitles(p => new Map([...p, [nt, now]]));
    } catch (err) {
      console.warn('[DeckSpaceModal ProFeed]', err.message);
      setJobFeedItems(p => ({ ...p, [nt]: [] }));
    } finally { setJobFeedLoading(false); }
  }, [userId, jobFeedFetchedTitles]);





  /* ── Career / Super map modals ───────────────────────────────────────── */
  const [careerMapSubject, setCareerMapSubject] = useState(null);
  const [careerMapJobs, setCareerMapJobs]       = useState([]);
  const [careerMapLoading, setCareerMapLoading] = useState(false);
  const [superMapSubject, setSuperMapSubject]   = useState(null);
  const [superMapActivities, setSuperMapActivities] = useState([]);
  const [superMapLoading, setSuperMapLoading]   = useState(false);

const openCareerMap = useCallback(async (subject) => {
    if (!subject) return;
    setCareerMapSubject(subject);
    const now = Date.now();
    const cachedRow = await getJobposts(effectiveUserId, subject);
    if (cachedRow && now - (cachedRow.timestamp || 0) < JOBPOSTS_CACHE_TTL && Array.isArray(cachedRow.data)) {
      setCareerMapJobs(cachedRow.data); setCareerMapLoading(false); return;
    }
    setCareerMapJobs([]); setCareerMapLoading(true);
    try {
      const res = await fetch(`/api/questions/jobposts/subject/${encodeURIComponent(subject)}`, { headers: getAuthHeaders() });
      const flat = res.ok ? (await res.json()) : [];
      setCareerMapJobs(Array.isArray(flat) ? flat : []);
      await saveJobposts(effectiveUserId, subject, flat);
    } catch { setCareerMapJobs([]); } finally { setCareerMapLoading(false); }
  }, [effectiveUserId]);

const openSuperMap = useCallback(async (subject) => {
    if (!subject) return;
    setSuperMapSubject(subject);
    const now = Date.now();
    const cache = await getSuperActivities(effectiveUserId);
    const key = subject.toLowerCase();
    const cached = cache.data?.[key];
    if (cached !== undefined && now - (cache.timestamp || 0) < SUPER_CACHE_TTL && Array.isArray(cached)) {
      setSuperMapActivities(cached); setSuperMapLoading(false); return;
    }
    setSuperMapActivities([]); setSuperMapLoading(true);
    try {
      const res = await fetch(`/api/questions/supercurricular-activities?subjects=${encodeURIComponent(subject)}`, { headers: getAuthHeaders() });
      const flat = res.ok ? (await res.json()) : [];
      setSuperMapActivities(Array.isArray(flat) ? flat : []);
      const updatedData = { ...(cache.data || {}), [key]: flat };
      await saveSuperActivities(effectiveUserId, updatedData);
    } catch { setSuperMapActivities([]); } finally { setSuperMapLoading(false); }
  }, [effectiveUserId]);

  const toggleReasonTag = (tag) => {
  setReasonDraftTags((prev) =>
    prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
  );
};

const handleAddActivityClick = useCallback(() => {
  if (!chosenTopSubject) {
    modalNavigate('/dashboard', {
      openChooseSubjectModal: true,
      intendedDestination: {
        path: '/dashboard_enrich',
        state: {
          sidebarItem: 'supercurricular',
          fromDeckSpace: true,
          sortedScores: topScores || [],
          sortedPart3Scores: topPart3Scores || [],
          userEmail,
        },
      },
    });
    return;
  }
  modalNavigate('/dashboard_enrich', {
    sidebarItem: 'supercurricular',
    preselectedSuperSubject: chosenTopSubject,
    fromDeckSpace: true,
    sortedScores: topScores || [],
    sortedPart3Scores: topPart3Scores || [],
    chosenTopSubject,
    userEmail,
  });
}, [chosenTopSubject, modalNavigate, topScores, topPart3Scores, userEmail]);

const handleAddJobClick = useCallback(() => {
  if (!chosenTopSubject) {
    modalNavigate('/dashboard', {
      openChooseSubjectModal: true,
      intendedDestination: {
        path: '/CareerDashboard',
        state: {
          sidebarItem: 'compass',
          directToCompass: true,
          fromDeckSpace: true,
          sortedScores: topScores || [],
          sortedPart3Scores: topPart3Scores || [],
          userEmail,
        },
      },
    });
    return;
  }
  modalNavigate('/CareerDashboard', {
    sidebarItem: 'compass',
    directToCompass: true,
    preselectedSubject: chosenTopSubject,
    fromDeckSpace: true,
    sortedScores: topScores || [],
    sortedPart3Scores: topPart3Scores || [],
    chosenTopSubject,
    userEmail,
  });
}, [chosenTopSubject, modalNavigate, topScores, topPart3Scores, userEmail]);

const openReasonEditor = (subject) => {
  const existing = subjectReasons[subject];
  setReasonDraftTags(existing?.tags || []);
  setReasonDraftText(existing?.text || '');
  setReasonEditorSubject(subject);
};

const saveSubjectReason = (subject, tags, text) => {
  const entry = { tags, text: text.trim() || null, updatedAt: Date.now() };
  const updated = { ...subjectReasons, [subject]: entry };
  setSubjectReasons(updated);

  saveSubjectReasons(effectiveUserId, updated).catch((err) => {
    console.warn('Could not save subject reason:', err.message);
  });
};

// ── Memoized activity cards (moved here to ensure all hooks have been called) ──
const activityCards = useMemo(() =>
  calendarEvents.map(event => (
    <ActivityCard
      key={event.id}
      event={event}
      activityStatuses={activityStatuses}
      topScores={topScores}
      topPart3Scores={topPart3Scores}
      chosenTopSubject={chosenTopSubject}
      userEmail={userEmail}
      onNavigate={modalNavigate}
    />
  )),
  [calendarEvents, activityStatuses, topScores, topPart3Scores, chosenTopSubject, userEmail, modalNavigate]
);

// ── Memoized job cards (after all state declarations, including jobFeedOpenCards) ──
const jobCards = useMemo(() =>
  portfolioXJobs.map(job => {
    const iStatus = (interviewStatuses[job.jobpost_id] || 'not_started').toLowerCase();
    const jobId = String(job.jobpost_id);
    const isOpen = jobFeedOpenCards[jobId] || false;
    const nt = normaliseCareerKey(job.title);
    const feedItems = jobFeedItems[nt] || [];
    const isLoading = jobFeedLoading && !jobFeedFetchedTitles.has(nt);

    return (
      <JobCard
        key={job.jobpost_id}
        job={job}
        interviewStatus={iStatus}
        extraCareerJobsList={extraCareerJobsList}
        jobSubjectMap={jobSubjectMap}
        chosenTopSubject={chosenTopSubject}
        topScores={topScores}
        topPart3Scores={topPart3Scores}
        userEmail={userEmail}
        skillProgress={getAverageSkillProgressForJob(job.jobpost_id)}
        jobFeedOpen={isOpen}
        onNavigate={modalNavigate}
        onToggleFeed={(id) => setJobFeedOpenCards(prev => ({ ...prev, [id]: !prev[id] }))}
        onLoadFeed={() => loadProfeedForJob(job.title)}
        renderProfeedPanel={
          <ProFeedPanel
            jobTitle={job.title}
            items={feedItems}
            isLoading={isLoading}
            onViewFull={(e) => {
              e.stopPropagation();
              modalNavigate('/CareerDashboard', {
                sidebarItem: 'careerfeed',
                preselectedCareer: job.title,
              });
            }}
          />
        }
      />
    );
  }),
  [
    portfolioXJobs,
    interviewStatuses,
    extraCareerJobsList,
    jobSubjectMap,
    chosenTopSubject,
    topScores,
    topPart3Scores,
    userEmail,
    getAverageSkillProgressForJob,
    jobFeedOpenCards,      // <-- now this is defined before the memo runs
    modalNavigate,
    loadProfeedForJob,
    jobFeedItems,
    jobFeedLoading,
    jobFeedFetchedTitles,
  ]
);

  /* ─────────────────────────────────────────────────────────────────────────
     JSX
  ──────────────────────────────────────────────────────────────────────────*/
  return (
    <>
    <div
      className="modal-overlay"
      onClick={closeModal}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.68)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100, backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="deckspace-modal-inner"
        style={{ background: 'linear-gradient(135deg, #0f1629c5, #1a2342cb)', color: 'white', borderRadius: '20px', width: 'min(97%, 1900px)', height: 'min(94vh, 1100px)', maxHeight: '96vh', display: 'grid', gridTemplateColumns: '70% 30%', overflow: 'hidden', border: '1px solid rgba(84,200,255,0.38)', boxShadow: '0 20px 80px rgba(0,180,255,0.22)' }}
      >
        <button
          className="close-mydeckspace"
          onClick={closeModal}
          style={{ position: 'absolute', top: '16px', right: '24px', background: 'none', border: 'none', color: '#ccc', fontSize: '2.6rem', cursor: 'pointer', zIndex: 15 }}
        >×</button>

        {/* ── LEFT COLUMN ─────────────────────────────────────────────── */}
        <div style={{ padding: '2.5rem 3.2rem 2rem', overflowY: 'auto', maxHeight: '96vh', position: 'relative' }} className="custom-scrollbar invisible-scroll deckspace-main-content">

          {/* Skeleton overlay */}
          {!deckSpaceReady && (
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0f1629, #1a2342)', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '20px', padding: '2.5rem 3.2rem', borderRadius: '20px 0 0 20px' }}>
              <div style={{ height: '36px', width: '220px', margin: '0 auto 8px', background: 'rgba(54,167,248,0.15)', borderRadius: '8px', animation: 'pulse 1.2s ease-in-out infinite' }} />
              <div style={{ display: 'flex', gap: '16px' }}>
                {[0,1].map(i => <div key={i} style={{ flex: i===0?'0 0 35%':1, height: '60px', background: 'rgba(54,167,248,0.10)', borderRadius: '12px', animation: `pulse 1.2s ease-in-out infinite ${i*0.1}s` }} />)}
              </div>
              {[0,1,2].map(i => <div key={i} style={{ height: i===0?'24px':'180px', width: i===0?'280px':'100%', background: 'rgba(54,167,248,0.08)', borderRadius: '12px', animation: `pulse 1.2s ease-in-out infinite ${i*0.1}s` }} />)}
              <style>{`@keyframes pulse { 0%,100%{opacity:0.6} 50%{opacity:1} }`}</style>
            </div>
          )}

          <h2 className="deckspace-title" style={{ margin: '0 0 2.2rem', marginTop: '-20px', color: '#43aefc', fontSize: '2.4rem', textAlign: 'center', fontWeight: 'bold', letterSpacing: '-0.5px' }}>
            My deckSPACE
          </h2>
     <div className="all-answers-saved-note-deck" style={{fontStyle: 'normal', marginTop: '-30px',  marginBottom: '0px', textAlign: 'center', color: '#b6b6b68e', fontSize: '14px' }}>
          <p>Your Roadmaps are saved locally in this browser only.</p>
        </div>
          {/* Subject + Grade/Country */}
          <div className="deckspace-subject-school" style={{ display: 'flex', gap: '1rem', marginBottom: '2.5rem' }}>
            <div className="deckspace-subject" style={{ display: 'flex', width: '35%', flexDirection: 'column' }}>
              <div  className="deckspace-subject-label" style={{ fontSize: '1rem', color: '#a0e7ff', marginBottom: '0.8rem', fontWeight: 500 }}>Chosen Higher Education Focus</div>
              <div className="deckspace-card-hover" style={{ flex: 1, minWidth: '400px', background: 'rgba(2,139,232,0.08)', border: '1px solid rgba(2,139,232,0.35)', borderRadius: '12px', padding: '0.8rem 1.5rem', maxHeight: '60px', transition: 'all 0.22s ease' }}>
                {chosenTopSubject ? (
                  <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#ffffff' }}>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setModalActiveTab('A-Levels');
                        setAcadFeedOpenModal(false);
                        fetchFacetData(chosenTopSubject);
                        setModalTooltip({ visible: true, x: Math.min(rect.right + 20, window.innerWidth - 600), y: rect.top - 10 });
                      }}
                      style={{ color: '#ffffff', cursor: 'pointer', transition: 'all 0.18s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#80deea'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#ffffff'; }}
                    >
                      <FaGraduationCap style={{ fontSize: '1.8rem' }} /> {chosenTopSubject}
                    </span>
                  </div>
                ) : (
                  <div style={{ color: '#888', fontStyle: 'italic', fontSize: '1.15rem' }}>No top subject selected yet</div>
                )}
              </div>
            </div>

            <div className="deckspace-school" style={{ display: 'flex', width: '65%', flexDirection: 'column' }}>
              <div className="deckspace-subject-label"  style={{ fontSize: '1rem', color: '#a0e7ff', marginBottom: '0.8rem', fontWeight: 500 }}>Current School / University Level</div>
              <div style={{ flex: 1, minWidth: '320px', background: 'rgba(54,167,248,0.06)', border: '1px solid rgba(54,167,248,0.3)', borderRadius: '12px', padding: '0.5rem 0.7rem', maxHeight: '60px', transition: 'all 0.22s ease' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(2,139,232,0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <span style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                  <div style={{ position: 'relative', width: '70%' }}>
                    <select value={selectedGrade} onChange={e => handleSaveGrade(e.target.value)} style={{ width: '100%', padding: '0.5rem 1rem', paddingRight: '2.5rem', background: '#1e2a44', color: 'white', border: 'none', borderRadius: '8px', appearance: 'none', height: '40px' }}>
                      {gradeLevels.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'white', fontSize: '0.75rem' }}>▼</span>
                  </div>
                  <div style={{ position: 'relative', width: '100%' }}>
<select value={selectedCountry || ''} onChange={e => setSelectedCountry(e.target.value)}                      style={{ width: '100%', padding: '0.5rem 1rem', paddingRight: '2.5rem', fontSize: '1.0rem', background: '#1e2a44', color: 'white', border: 'none', borderRadius: '8px', outline: 'none', height: '40px', appearance: 'none' }}>
                      <option value="">Select country</option>
                      {Object.entries(countries).sort(([,a],[,b]) => a.name.localeCompare(b.name)).map(([code, c]) => (
                        <option key={code} value={code}>{c.emoji} {c.name}</option>
                      ))}
                    </select>
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'white', fontSize: '0.75rem' }}>▼</span>
                  </div>
                </span>
              </div>
            </div>
          </div>

          {/* Supercurricular Carousel */}
<div className="deckspace-carousel-section" style={{ marginTop: '2.5rem', position: 'relative' }}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
    <h3 style={{ color: '#a0e7ff', fontSize: '1rem', margin: 0, textAlign: 'left' }}>Planned SuperCurricular Activities</h3>
    <AddSectionButton label="Add Activity" onClick={handleAddActivityClick} />
  </div>
  {calendarEvents.length === 0 ? (
    <div
      onClick={handleAddActivityClick}
      style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: '2.5rem 0', cursor: 'pointer' }}
    >
      No activities added to your calendar yet — click here or "Add Activity" above to get started.
    </div>
  ) : (
    <div style={{ position: 'relative' }}>
      <button className="carousel-arrow carousel-arrow-left" onClick={() => { const c = document.getElementById('sc-carousel-ds'); if (c) c.scrollBy({ left: -340, behavior: 'smooth' }); }}
        style={{ position: 'absolute', left: '-20px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(2,140,232,0.24)', color: 'white', border: 'none', borderRadius: '50%', width: '48px', height: '48px', fontSize: '1.6rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}>←</button>

<div id="sc-carousel-ds" className="custom-scrollbar deckspace-carousel-track" style={{ display: 'flex', overflowX: 'auto', gap: '1.3rem', padding: '1rem 0', scrollBehavior: 'smooth', scrollbarWidth: 'none' }}>
  {activityCards}
</div>

      <button className="carousel-arrow carousel-arrow-right" onClick={() => { const c = document.getElementById('sc-carousel-ds'); if (c) c.scrollBy({ left: 340, behavior: 'smooth' }); }}
        style={{ position: 'absolute', right: '-20px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(2,140,232,0.24)', color: 'white', border: 'none', borderRadius: '50%', width: '48px', height: '48px', fontSize: '1.6rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}>→</button>
    </div>
  )}
</div>

          {/* UniStatement + PortfolioX + InterviewPro cards */}
          <div style={{ marginTop: '0rem' }}>
            {chosenTopSubject ? (
<div className="deckspace-status-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem' }}>
                  {[
                  { key: 'unistatement', title: 'UniStatement', icon: <FaEdit />,
                    getStatus: () => psStatus,
                    onClick: () => modalNavigate('/dashboard_enrich', { sidebarItem: 'personalstatement', preselectedSubject: chosenTopSubject, fromDeckSpace: true, sortedScores: topScores || [], sortedPart3Scores: topPart3Scores || [], chosenTopSubject, userEmail }) },
              { key: 'portfoliox', title: 'PortfolioX', icon: <FaCertificate />,
                    getStatus: () => portfolioXStatus || 'not_started',
                    onClick: () => modalNavigate('/CareerDashboard', { sidebarItem: 'portfoliox', forcePortfolioX: true, preselectedSubject: chosenTopSubject, fromDeckSpace: true, sortedScores: topScores || [], sortedPart3Scores: topPart3Scores || [], chosenTopSubject, userEmail }) },
                ].map(item => {
                  const raw = item.getStatus();
                  const status = typeof raw === 'string' ? raw.toLowerCase() : 'not_started';
                  const statusConfig = {
                    not_started: { label: 'Not Started', color: '#888888ff', bg: 'rgba(158,158,158,0.14)', border: 'rgba(158,158,158,0.3)' },
                    in_progress: { label: 'In Progress', color: '#00dbff',   bg: 'rgba(33,150,243,0.18)', border: 'rgba(33,150,243,0.4)' },
                    completed:   { label: 'Completed',   color: '#00ff00',   bg: 'rgba(76,175,80,0.18)',  border: 'rgba(76,175,80,0.4)'  },
                  };
                  const sc = statusConfig[status] || statusConfig.not_started;
                  return (
<div key={item.key} className="deckspace-status-card" style={{ background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: '12px', padding: '0.8rem 1.4rem', display: 'flex', flexDirection: 'column', transition: 'all 0.22s ease', maxHeight: '60px' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(2,139,232,0.25)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button onClick={item.onClick} className="combined" style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', color: '#ffffff', cursor: 'pointer', textAlign: 'left', boxShadow: 'none', fontSize: '1.5rem' }}>
                          {item.icon} {item.title}
                        </button>
                        <div style={{ padding: '5px 12px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: '600', backgroundColor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap' }}>{sc.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: '1.8rem 0', background: 'rgba(40,40,60,0.3)', borderRadius: '10px', border: '1px dashed #555' }}>
                Select your top subject first to unlock UniStatement and PortfolioX
              </div>
            )}
          </div>

{/* Added Jobs Carousel */}
<div className="deckspace-carousel-section" style={{ margin: '2.5rem 0 1.5rem 0', position: 'relative' }}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
    <h3 style={{ color: '#a0e7ff', fontSize: '1rem', margin: 0, textAlign: 'left' }}>My Added Jobs</h3>
    <AddSectionButton label="Add Job" onClick={handleAddJobClick} />
  </div>
  {portfolioXJobs.length === 0 ? (
    <div
      onClick={handleAddJobClick}
      style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: '1rem 0', cursor: 'pointer' }}
    >
      No jobs added yet — click here or "Add Job" above to explore Compass.
    </div>
  ) : (
    <div style={{ position: 'relative' }}>
      <button className="carousel-arrow carousel-arrow-left" onClick={() => { const c = document.getElementById('jobs-carousel-ds'); if (c) c.scrollBy({ left: -360, behavior: 'smooth' }); }}
        style={{ position: 'absolute', left: '-24px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(2,140,232,0.24)', color: 'white', border: 'none', borderRadius: '50%', width: '52px', height: '52px', fontSize: '1.8rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.45)' }}>←</button>

     <div id="jobs-carousel-ds" className="custom-scrollbar deckspace-carousel-track" style={{ display: 'flex', overflowX: 'auto', gap: '1.4rem', padding: '1rem 0', scrollBehavior: 'smooth', scrollbarWidth: 'none' }}>
  {jobCards}
</div>

      <button className="carousel-arrow carousel-arrow-right" onClick={() => { const c = document.getElementById('jobs-carousel-ds'); if (c) c.scrollBy({ left: 360, behavior: 'smooth' }); }}
        style={{ position: 'absolute', right: '-24px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(2,140,232,0.24)', color: 'white', border: 'none', borderRadius: '50%', width: '52px', height: '52px', fontSize: '1.8rem', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.45)' }}>→</button>
    </div>
  )}
</div>

          {/* Facet tooltip */}
          {modalTooltip.visible && facetData && (
            <div className="modal-tooltip-deckspace"
              style={{ position: 'fixed', left: `${modalTooltip.x}px`, top: `${modalTooltip.y}px`, backgroundColor: 'rgba(30,30,40,0.96)', color: 'white', padding: '18px 22px', borderRadius: '12px', border: '1px solid rgba(0,225,255,0.55)', maxWidth: '580px', width: '580px', zIndex: 999999, boxShadow: '0 12px 50px rgba(0,0,0,0.7)', pointerEvents: 'auto', fontSize: '0.97rem', lineHeight: 1.55 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(54,167,248,0.35)' }}>
                <div style={{ flex: 1 }}>
                  <a href={`https://www.bachelorsportal.com/search/bachelor?kw-what=${encodeURIComponent(modalCurrentFacet)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#40c4ff', textDecoration: 'none', fontWeight: '600', fontSize: '1.28rem' }}>
                    {modalCurrentFacet}
                  </a>
                </div>
<div className="facet-tooltip-icons" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                    <button onClick={e => { e.stopPropagation(); setModalTooltip(p => ({ ...p, visible: false })); setAcadFeedOpenModal(false); openCareerMap(modalCurrentFacet); }}
                    style={{ marginRight: '-20px', background: 'none', border: 'none', color: '#ffaa00', cursor: 'pointer', fontSize: '1.6rem', padding: '4px', lineHeight: 1 }} title="View Career Map">
                    <FaRegCompass />
                  </button>
                  <button onClick={e => { e.stopPropagation(); if (acadFeedOpenModal) { setAcadFeedOpenModal(false); } else { setAcadFeedOpenModal(true); loadAcadFeedForSubject(modalCurrentFacet); } }}
                    style={{ marginRight: '-40px', background: acadFeedOpenModal ? 'rgba(255,170,0,0.18)' : 'none', border: acadFeedOpenModal ? '1px solid #ffaa00' : 'none', borderRadius: '6px', color: '#ffaa00', cursor: 'pointer', fontSize: '1.6rem', padding: '4px', lineHeight: 1 }}>
                    <FaRss />
                  </button>
                    <button
    onClick={(e) => { e.stopPropagation(); openReasonEditor(modalCurrentFacet); }}
    style={{ background: 'none', marginLeft: '18px', marginRight: '-30px', border: 'none', color: '#ffaa00', cursor: 'pointer', fontSize: '1.6rem', padding: '4px' }}
    title={subjectReasons[modalCurrentFacet] ? 'Edit why you like this subject' : 'Why do you like this subject?'}
  >
    <FaPenSquare />
  </button>
                  <button onClick={() => { setModalTooltip(p => ({ ...p, visible: false })); setModalCurrentFacet(null); setModalActiveTab('A-Levels'); setAcadFeedOpenModal(false); }}
                    style={{ background: 'none', border: 'none', color: '#ccc', fontSize: '1.7rem', cursor: 'pointer', marginLeft: '6px' }}>×</button>
                </div>
              </div>

              {facetData.note && <p style={{ margin: '0 0 18px 0', color: '#d0f0ff' }}>{facetData.note}</p>}

              {facetData.options?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {['A-Levels', 'IB', 'AP', 'Supercurricular'].map(tab => (
                      <button key={tab} onClick={e => { e.stopPropagation(); if (tab === 'Supercurricular') { setModalTooltip(p => ({ ...p, visible: false })); setAcadFeedOpenModal(false); openSuperMap(modalCurrentFacet); } else { setModalActiveTab(tab); } }}
                        style={{ padding: '6px 14px', backgroundColor: tab === 'Supercurricular' ? '#ffaa001a' : modalActiveTab === tab ? 'rgba(2,139,232,0.35)' : 'rgba(2,139,232,0.12)', color: tab === 'Supercurricular' ? '#ffaa00' : modalActiveTab === tab ? '#ffffff' : '#40c4ff', border: tab === 'Supercurricular' ? '1px solid #ffaa00' : modalActiveTab === tab ? '1px solid #40c4ff' : '1px solid rgba(64,196,255,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.92rem', fontWeight: modalActiveTab === tab || tab === 'Supercurricular' ? '600' : '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {tab === 'Supercurricular' && <FaBolt style={{ fontSize: '1.1rem', color: '#ffaa00' }} />}
                        {tab}
                      </button>
                    ))}
                    <a href={getAISearchLink(modalCurrentFacet)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                      style={{ padding: '5px 10px', backgroundColor: '#ffaa0002', color: 'white', border: '2px solid transparent', backgroundImage: 'linear-gradient(#1e1e2e, #1e1e2e), linear-gradient(90deg, rgb(0,255,183), rgb(0,170,255))', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none', fontWeight: '600' }}>
                      AI Era Relevance
                    </a>
                  </div>
                  <div style={{ maxHeight: '260px', overflowY: 'auto', paddingRight: '6px' }}>
                    {facetData.options.filter(opt => opt.name === modalActiveTab).map((opt, idx) => (
                      <div key={idx} style={{ marginBottom: '16px', padding: '12px', background: 'rgba(54,167,248,0.10)', borderRadius: '10px', border: '1px solid rgba(54,167,248,0.35)' }}>
                        <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '1.05rem' }}>
                          {getOptionLink(opt.name) ? (
                            <a href={getOptionLink(opt.name)} target="_blank" rel="noopener noreferrer" style={{ color: '#40c4ff', textDecoration: 'none' }}>{opt.name}</a>
                          ) : <span style={{ color: '#40c4ff' }}>{opt.name}</span>}
                        </div>
                        <div style={{ fontSize: '0.94rem', color: '#e0f0ff' }}>
                          {opt.sub_options?.map((sub, sIdx) => (
                            <div key={sIdx} style={{ marginBottom: '8px' }}>
                              <strong>{sub.name}:</strong> {sub.sub_sub_options?.map(s => s.name).filter(Boolean).join(', ') || 'None specified'}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

             {acadFeedOpenModal && (
  <div style={{ marginTop: '14px', borderTop: '1px solid rgba(255,170,0,0.3)', paddingTop: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', color: '#ffaa00', fontWeight: 600, fontSize: '0.88rem' }}><FaRss /> Latest Articles</div>
    <AcadFeedPanel
      subject={modalCurrentFacet}
      items={acadFeedItems[modalCurrentFacet] || []}
      isLoading={acadFeedLoading && !acadFeedFetchedSubjects.has(modalCurrentFacet)}
      onViewFull={(e) => {
        e.stopPropagation();
        modalNavigate('/enrichfeed', {
          preselectedSubject: modalCurrentFacet,
          fromDashboard: true,
        });
      }}
    />
  </div>
)}
            </div>
          )}
        </div>

 {/* ── RIGHT COLUMN: Personalised Roadmap ───────────────────────────────────── */}
<div style={{ background: 'rgba(18, 26, 48, 0.38)', borderLeft: '1px solid rgba(84,187,248,0.28)', padding: '2.5rem 2.2rem', display: 'flex', flexDirection: 'column', overflowY: 'auto', maxHeight: '96vh' }} className="custom-scrollbar deckSPACE-roadmap-section">

  <h3 style={{ margin: '0 0 1.4rem 0', color: '#43aefc', fontSize: '1.2rem', fontWeight: 600 }}>
    🎯 My Personalised  3-Year Roadmap
  </h3>

  {/* Job Target Selector */}
  <div className="roadmap-job-selector" style={{ marginBottom: '1.2rem' }}>
    <label className="deckspace-subject-label"  style={{ display: 'block', color: '#a0e7ff', marginBottom: '6px', fontSize: '1rem', fontWeight: 500 }}>
      Target Early Career
    </label>
    <select 
      value={selectedRoadmapJob?.jobpost_id || ''}
      onChange={(e) => {
        const job = portfolioXJobs.find(j => String(j.jobpost_id) === e.target.value);
        setSelectedRoadmapJob(job || null);
      }}
      style={{ width: '100%', padding: '10px 12px', background: '#1e2a44', color: 'white', fontSize: '1rem', border: '1px solid #445577', borderRadius: '8px' }}
    >
      <option value="">Select from your added jobs</option>
      {portfolioXJobs.map(job => (
        <option key={job.jobpost_id} value={job.jobpost_id}>
          {job.title}
        </option>
      ))}
    </select>
  </div>

  {/* Generate Button */}
<button
    className="roadmap-generate-btn"
    onClick={handleGenerateRoadmap}
    disabled={!selectedRoadmapJob}
    style={{
      width: '100%', padding: '10px', background: selectedRoadmapJob ? 'linear-gradient(135deg, #1d9bf0 0%, #4db3ff 100%)' : '#334155',
      color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, 
      fontSize: '1rem', cursor: selectedRoadmapJob ? 'pointer' : 'not-allowed', 
      marginBottom: '1.8rem',
      boxShadow: selectedRoadmapJob ? '0 4px 15px rgba(139,92,246,0.3)' : 'none'
    }}
  >
    {selectedRoadmapJob ? `Generate Plan` : 'Select a job above'}
  </button>

  {/* Editable Roadmap Content */}
<div className="roadmap-textarea-wrapper" style={{ marginBottom: '1.8rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
    <textarea
    className='space-scroll'
      value={roadmapContent}
      onChange={(e) => setRoadmapContent(e.target.value)}
      placeholder="Paste the generated 3-year roadmap from Grok here...&#10;&#10;You can also write your own notes or edits."
      style={{ 
        flex: 1,
        width: '100%', 
        minHeight: '180px',
        background: 'rgba(8,14,32,0.9)', 
        borderRadius: '12px', 
        padding: '1.6rem', 
        border: '1px solid rgba(139,92,246,0.3)',
        color: 'white',
        fontSize: '0.97rem',
        lineHeight: 1.7,
        resize: 'vertical',
        fontFamily: 'inherit'
      }}
    />
  </div>

  {/* Reflection Section */}
<div className="roadmap-reflection-section" style={{ borderTop: '1px solid rgba(84,187,248,0.25)', paddingTop: '1.6rem' }}>
    <div style={{ color: '#43aefc', marginBottom: '1.6rem', fontSize: '1.2rem', fontWeight: 600 }}>💡 Help me personalise future roadmaps</div>

    {reflectionQuestions.map((q, i) => (
      <div key={i} className="reflection-item" style={{ marginBottom: '1.4rem' }}>
        <label style={{ display: 'block', marginBottom: '6px', color: '#a0e7ff', fontSize: '1rem', fontWeight: '500' }}>
          {q.prompt}
        </label>
        {q.type === 'textarea' ? (
          <textarea
          className='space-scroll'
            value={reflections[q.key] || ''}
            onChange={(e) => handleReflectionChange(q.key, e.target.value)}
            style={{ width: '100%', height: '40px', background: '#1a2338', border: '1px solid #445577', borderRadius: '8px', color: 'white', padding: '10px' }}
          />
        ) : (
          <StarRating rating={reflections[q.key] || 0} onChange={(val) => handleReflectionChange(q.key, val)} />
        )}
      </div>
    ))}

  </div>
</div>
      </div>
    </div>

    {/* Career map modal — self-contained inside DeckSpaceModal */}
    {careerMapSubject && (
      <SubjectCareerMapModal
        subject={careerMapSubject}
        jobposts={careerMapJobs}
        loading={careerMapLoading}
        onClose={() => { setCareerMapSubject(null); setCareerMapJobs([]); setCareerMapLoading(false); }}
        onJobClick={(job) => modalNavigate('/CareerDashboard', { sidebarItem: 'compass', directToCompass: true, preselectedSubject: careerMapSubject, preselectedJobId: job.jobpost_id })}
      />
    )}
    {superMapSubject && (
      <SubjectSuperCurricularMapModal
        subject={superMapSubject}
        activities={superMapActivities}
        loading={superMapLoading}
        onClose={() => { setSuperMapSubject(null); setSuperMapActivities([]); setSuperMapLoading(false); }}
        onActivityClick={(activity) => modalNavigate('/dashboard_enrich', { sidebarItem: 'supercurricular', preselectedSuperSubject: superMapSubject, preselectedCategory: activity.category, preselectedActivityId: activity.supercurricular_id, scrollToActivity: true })}
      />
    )}

    {/* ── Why do you like this subject? Editor ── */}
{reasonEditorSubject && (
<div className="modal reason-editor-overlay" style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999999 }}>
    <div className="reason-editor-card" style={{ backgroundColor: "#050f27e6", padding: "34px", borderRadius: "14px", color: "white", width: "560px", maxWidth: "92%", border: "1px solid #ffaa007d" }}>
      <h3 style={{ margin: "0 0 6px", color: "#ffaa00" }}>
        Why do you like {reasonEditorSubject}?
      </h3>
      <p style={{ margin: "0 0 26px", fontSize: "0.88rem", color: "#aaa" }}>
        Optional — saved locally on this device only.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '26px' }}>
        {SUBJECT_REASON_TAGS.map((tag) => {
          const active = reasonDraftTags.includes(tag);
          return (
            <button
              key={tag}
              onClick={() => toggleReasonTag(tag)}
              style={{
                padding: '6px 11px', fontSize: '12.5px', borderRadius: '999px',
                border: '1px solid #ffaa00',
                background: active ? '#ffaa00' : 'transparent',
                color: active ? '#0b1021' : '#ffaa00',
                cursor: 'pointer',
              }}
            >
              {tag}
            </button>
          );
        })}
      </div>

      <textarea
        value={reasonDraftText}
        onChange={(e) => setReasonDraftText(e.target.value)}
        placeholder="Anything else you want to remember..."
        rows={4}
        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #444', background: '#1a1a1a', color: 'white' }}
      />

      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}>
        <button onClick={() => setReasonEditorSubject(null)} style={{ padding: "9px 18px", background: "#444", color: "white", border: "none", borderRadius: "8px" }}>
          Cancel
        </button>
        <button 
          onClick={() => {
            saveSubjectReason(reasonEditorSubject, reasonDraftTags, reasonDraftText);
            setReasonEditorSubject(null);
          }}
          style={{ padding: "9px 22px", background: "#028be8", color: "white", border: "none", borderRadius: "8px", fontWeight: 600 }}
        >
          Save
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
})

export default DeckSpaceModal;

